import vbotService from "@/services/vbot.service"

const initialState = { 
    vbots: [],
    selectedVbot: null,
    phonebots: [],
    generatedPrompt: null
};

export const vbot = {
    namespaced: true,
    state: initialState,
    actions: {
        // ✅ STANDARD VBOT AKTIONEN
        async addVbot({ commit }, vbot) {
            try {
                const newVbot = await vbotService.addVbot(vbot);
                commit('addVbotSuccess', newVbot);
                return newVbot;
            } catch (error) {
                commit('addVbotError', error);
                throw error;
            }
        },

        async getVbots({ commit }) {
            try {
                const vbots = await vbotService.getVbots();
                commit('getVbotsSuccess', vbots);
                return vbots;
            } catch (error) {
                commit('getVbotsError', error);
                throw error;
            }
        },

        async getVbotById({ commit }, licenseKey) {
            try {
                const vbot = await vbotService.getVbotById(licenseKey);
                commit('getVbotSuccess', vbot);
                return vbot;
            } catch (error) {
                commit('getVbotError', error);
                throw error;
            }
        },

        async updateVbot({ commit }, vbot) {
            try {
                const updatedVbot = await vbotService.updateVbot(vbot);
                commit('updateVbotSuccess', updatedVbot);
                return updatedVbot;
            } catch (error) {
                commit('updateVbotError', error);
                throw error;
            }
        },

        async deleteVbot({ commit }, vbotId) {
            try {
                await vbotService.deleteVbot(vbotId);
                commit('deleteVbotSuccess', vbotId);
            } catch (error) {
                commit('deleteVbotError', error);
                throw error;
            }
        },

        // ✅ PHONEBOT AKTIONEN (FÜR SUPER-ADMIN)
        async getPhonebots({ commit }) {
            try {
                const phonebots = await vbotService.getPhonebots();
                commit('getPhonobotsSuccess', phonebots);
                return phonebots;
            } catch (error) {
                commit('getPhonobotsError', error);
                throw error;
            }
        },

        async updatePhonebot({ commit }, phonebot) {
            try {
                const updatedPhonebot = await vbotService.updatePhonebot(phonebot);
                commit('updatePhonebotSuccess', updatedPhonebot);
                return updatedPhonebot;
            } catch (error) {
                commit('updatePhonebotError', error);
                throw error;
            }
        },

        // ✅ PROMPT-GENERIERUNG
        async generatePrompt({ commit }, { vbotId, promptData }) {
            try {
                const response = await vbotService.generatePrompt(vbotId, promptData);
                commit('generatePromptSuccess', response.data);
                return response.data;
            } catch (error) {
                commit('generatePromptError', error.response?.data?.error || error.message);
                throw error;
            }
        },

        // ✅ GUEST VBOT AKTIONEN
        async getVbotByIdForGuest({ commit }, licenseKey) {
            try {
                const vbot = await vbotService.getVbotByIdForGuest(licenseKey);
                commit('getVbotSuccess', vbot);
                return vbot;
            } catch (error) {
                commit('getVbotError', error);
                throw error;
            }
        },

        async updateVbotByGuest({ commit }, vbot) {
            try {
                const updatedVbot = await vbotService.updateVbotByGuest(vbot);
                commit('updateVbotSuccess', updatedVbot);
                return updatedVbot;
            } catch (error) {
                commit('updateVbotError', error);
                throw error;
            }
        }
    },
    
    mutations: {
        // ✅ STANDARD VBOT MUTATIONEN
        addVbotSuccess(state, vbot) {
            state.vbots.push(vbot);
        },
        
        addVbotError(state, error) {
            console.error('Error adding vbot:', error);
        },
        
        getVbotsSuccess(state, vbots) {
            state.vbots = vbots;
        },
        
        getVbotsError(state, error) {
            console.error('Error loading vbots:', error);
            state.vbots = [];
        },
        
        getVbotSuccess(state, vbot) {
            state.selectedVbot = vbot;
        },
        
        getVbotError(state, error) {
            console.error('Error loading vbot:', error);
            state.selectedVbot = null;
        },
        
        updateVbotSuccess(state, updatedVbot) {
            const index = state.vbots.findIndex(vbot => vbot.id === updatedVbot.id);
            if (index !== -1) {
                state.vbots.splice(index, 1, updatedVbot);
            }
            state.selectedVbot = updatedVbot;
        },
        
        updateVbotError(state, error) {
            console.error('Error updating vbot:', error);
        },
        
        deleteVbotSuccess(state, vbotId) {
            state.vbots = state.vbots.filter(vbot => vbot.id !== vbotId);
        },
        
        deleteVbotError(state, error) {
            console.error('Error deleting vbot:', error);
        },

        // ✅ PHONEBOT MUTATIONEN (SUPER-ADMIN)
        getPhonobotsSuccess(state, phonebots) {
            state.phonebots = phonebots;
        },
        
        getPhonobotsError(state, error) {
            console.error('Error loading phonebots:', error);
            state.phonebots = [];
        },
        
        updatePhonebotSuccess(state, updatedPhonebot) {
            // Update in Phonebot-Liste (Super-Admin-Ansicht)
            const index = state.phonebots.findIndex(bot => bot.id === updatedPhonebot.id);
            if (index !== -1) {
                state.phonebots.splice(index, 1, updatedPhonebot);
            }
            
            // Falls auch in normaler VBot-Liste vorhanden, dort auch updaten
            const vbotIndex = state.vbots.findIndex(bot => bot.id === updatedPhonebot.id);
            if (vbotIndex !== -1) {
                state.vbots.splice(vbotIndex, 1, updatedPhonebot);
            }
            
            // SelectedVbot updaten falls es dasselbe ist
            if (state.selectedVbot && state.selectedVbot.id === updatedPhonebot.id) {
                state.selectedVbot = updatedPhonebot;
            }
        },
        
        updatePhonebotError(state, error) {
            console.error('Error updating phonebot:', error);
        },

        // ✅ PROMPT-GENERIERUNG MUTATIONEN
        generatePromptSuccess(state, promptData) {
            state.generatedPrompt = promptData;
        },
        
        generatePromptError(state, error) {
            state.generatedPrompt = null;
            console.error('Prompt generation failed:', error);
        },
        
        clearGeneratedPrompt(state) {
            state.generatedPrompt = null;
        }
    },
    
    getters: {
        getVbots(state) {
            return state.vbots;
        },
        
        getSelectedVbot(state) {
            return state.selectedVbot;
        },

        getPhonebots(state) {
            return state.phonebots;
        },
        
        getGeneratedPrompt(state) {
            return state.generatedPrompt;
        }
    }
}